/**
 * chase_camera.ts — a chase camera that TRAILS a turn and BANKS with it (physics follow-up A, 2026-10-05). PURE.
 *
 * The hovercraft's chase camera was rigidly the hull's yaw (the rig's yaw IS the hull's, the camera a child of the rig)
 * and atmospheric flight copied `flyQuat` every frame: a turn rotated the whole world around a pinned hull, which reads
 * as the camera being bolted on (`audit-feel.md` G2). This is the classic critically damped follow:
 *
 *   ψ̈_cam = ω² (ψ_hull − ψ_cam) − 2ω ψ̇_cam          (ζ = 1: no overshoot, the fastest return without one)
 *
 * so in a steady turn at rate r the camera settles 2r/ω BEHIND the hull (the hull swings a little side-on — the look),
 * capped at CHASE_LAG_MAX; when the turn ends it closes the gap with no swing past centre. A ROLL from the camera's own
 * turn rate banks the picture into the turn (positive yaw rate = a left turn = the left side down; three's +Z roll).
 *
 * THE CAMERA ONLY: the hull, its guns, its collision probes and the server row all keep the true heading — the module
 * applies the lag as a yaw OFFSET of the camera relative to the rig and orbits the chase position by the same offset.
 *
 * FRAME-RATE INDEPENDENT: each step integrates the spring EXACTLY for a target that moves linearly over the step (the
 * offset from the steady trajectory x_p = T(t) − 2r/ω obeys the homogeneous equation, whose closed form is
 * y(t) = (y₀ + (ẏ₀ + ω y₀) t) e^{−ωt}), so a steady turn reaches the same lag at 30, 60 or 144 Hz; the roll eases with
 * e^{−k·dt}. Scaled by the camera-motion row (`camera_comfort.ts`) by the caller; never applied in a headset.
 */

/** 1/s — the spring's natural frequency. Steady lag = 2r/ω: at cruise (r ≈ 0.7 rad/s) ≈ 0.2 rad. */
export const CHASE_LAG_OMEGA = 7;
/** The camera never trails the hull by more than this (rad ≈ 17°) — a slow pivot at full lock would otherwise swing
 *  the hull side-on. */
export const CHASE_LAG_MAX_RAD = 0.3;
/** Roll per rad/s of the camera's own turn rate, and its cap (rad ≈ 6°) — a nudge, never a horizon flip. */
export const CHASE_BANK_GAIN = 0.1;
export const CHASE_BANK_MAX_RAD = 0.105;
/** 1/s — how fast the roll follows the turn rate. */
export const CHASE_BANK_RATE = 5;
/** A target jump bigger than this in one step (a teleport, a respawn, a resume) re-seats the camera instead of swinging. */
export const CHASE_SNAP_RAD = 1.2;

export interface ChaseState {
  /** The camera's yaw (continuous, unwrapped) and its yaw rate. */
  yaw: number; rate: number;
  /** The target (hull) yaw it last saw, continuous with `yaw`. */
  target: number;
  roll: number;
  init: boolean;
}
export function newChase(): ChaseState { return { yaw: 0, rate: 0, target: 0, roll: 0, init: false }; }
/** Seat the camera on the hull (mount, dismount, cockpit, XR): no lag, no roll. */
export function resetChase(s: ChaseState, yaw: number): void {
  const y = Number.isFinite(yaw) ? yaw : 0;
  s.yaw = y; s.target = y; s.rate = 0; s.roll = 0; s.init = true;
}

/** Wrap an angle to (−π, π] — closed form (never a loop on an unbounded input: THE HOSTILE-YAW LAW). */
export function wrapPi(a: number): number {
  if (!Number.isFinite(a)) return 0;
  const t = Math.PI * 2;
  let r = a - t * Math.floor((a + Math.PI) / t);
  if (r <= -Math.PI) r += t;
  return r;
}

export interface ChaseTune { omega: number; maxLag: number; bankGain: number; bankMax: number; bankRate: number }
export const CHASE_TUNE: Readonly<ChaseTune> = Object.freeze({
  omega: CHASE_LAG_OMEGA, maxLag: CHASE_LAG_MAX_RAD, bankGain: CHASE_BANK_GAIN, bankMax: CHASE_BANK_MAX_RAD, bankRate: CHASE_BANK_RATE,
});

/**
 * Advance the chase by `dt` toward the hull's yaw `targetYaw`. Returns the camera's yaw OFFSET from the hull (`lag`,
 * rad, wrapped; negative while the camera trails a left turn) and its `roll` (rad). Mutates the state.
 */
export function stepChase(s: ChaseState, targetYaw: number, dt: number, tune: ChaseTune = CHASE_TUNE): { lag: number; roll: number } {
  const T = Number.isFinite(targetYaw) ? targetYaw : 0;
  if (!s.init || !(dt > 0) || !Number.isFinite(dt)) {
    if (!s.init) resetChase(s, T);
    return { lag: wrapPi(s.yaw - s.target), roll: s.roll };
  }
  const dT = wrapPi(T - s.target);
  if (Math.abs(dT) > CHASE_SNAP_RAD) { resetChase(s, T); return { lag: 0, roll: 0 }; }
  const T0 = s.target, T1 = T0 + dT;
  const w = Math.max(1e-3, tune.omega);
  const r = dT / dt;                                 // the target's rate over this step
  const y0 = s.yaw - (T0 - 2 * r / w);               // offset from the steady trajectory at the start
  const v0 = s.rate - r;
  const e = Math.exp(-w * dt);
  const y1 = (y0 + (v0 + w * y0) * dt) * e;
  const v1 = (v0 - w * (v0 + w * y0) * dt) * e;
  let x1 = T1 - 2 * r / w + y1;
  let rate = r + v1;
  const lag = x1 - T1;
  if (Math.abs(lag) > tune.maxLag) { x1 = T1 + Math.sign(lag) * tune.maxLag; rate = r; }
  // Keep the pair near (−π, π] without breaking their relation (shift both by the same turn).
  const shift = T1 - wrapPi(T1);
  s.yaw = x1 - shift; s.target = T1 - shift; s.rate = rate;
  const want = Math.max(-tune.bankMax, Math.min(tune.bankMax, tune.bankGain * rate));
  s.roll += (want - s.roll) * (1 - Math.exp(-tune.bankRate * dt));
  return { lag: wrapPi(s.yaw - s.target), roll: s.roll };
}

/** Rotate a rig-local chase position (behind the hull at +Z) by the camera's yaw offset about +Y (three's rotation.y). */
export function orbitChase(p: { x: number; y: number; z: number }, lag: number): { x: number; y: number; z: number } {
  const c = Math.cos(lag), sn = Math.sin(lag);
  return { x: p.x * c + p.z * sn, y: p.y, z: -p.x * sn + p.z * c };
}
